import React from "react";
import { PlxsText, PlxsChip } from "@plexlogic/plexus-ui";
import { toSentenceCase } from "../../utils/stringUtils";
import { getStatusConfig } from "../../utils/actionRegistry";
import { formatDateTime } from "../../utils/dateTimeUtils";

interface ScrutineerSettings {
  scrutineer_required: boolean;
  scrutineer_name?: string;
  scrutineer_email?: string;
  scrutineer_job_title?: string;
  scrutineer_confirmation?: boolean;
}

interface DrawStatusInfoProps {
  status: string;
  date: string;
  time: string;
  accountName: string;
  drawNo: string;
  drawName: string;
  organisation: string;
  entryCount: number;
  prizeTypes: number;
  prizeWinners: number;
  reserveWinners: number;
  scheduledDateTime?: string; // ISO string for scheduled draws
  completedDateTime?: string; // ISO string for completed draws
  executionError?: string; // Error message for failed draws
  scrutineerSettings?: ScrutineerSettings; // Scrutineer information
  paymentRequired?: boolean;
}

const DrawStatusInfo: React.FC<DrawStatusInfoProps> = ({
  status,
  date,
  time,
  accountName,
  drawNo,
  drawName,
  organisation,
  entryCount,
  prizeTypes,
  prizeWinners,
  reserveWinners,
  scheduledDateTime,
  completedDateTime,
  scrutineerSettings,
  paymentRequired,
}) => {
  const statusConfig = getStatusConfig(status);
  
  const formatScheduledDate = (isoString: string) => {
    // Use centralized datetime formatting (browser's local timezone)
    return formatDateTime(isoString);
  };

  return (
    <div className="draw-detail-card">
      <h3>{toSentenceCase("Details")}</h3>

      <div className="status-details-grid">
        <PlxsText size="sm">Status:</PlxsText>
        <PlxsChip
          dataTestId="draw-detail-status-chip"
          label={statusConfig.label}
          tone={statusConfig.tone}
          {...(statusConfig.decorativeIcon && { decorativeIcon: statusConfig.decorativeIcon as any })}
        />

        {status.toLowerCase() === "scheduled" && scheduledDateTime && (
          <>
            <PlxsText size="sm">Due date:</PlxsText>
            <PlxsText size="sm">
              {formatScheduledDate(scheduledDateTime).date}{" "}
              {formatScheduledDate(scheduledDateTime).time}
            </PlxsText>
          </>
        )}

        {status.toLowerCase() === "completed" && completedDateTime && (
          <>
            <PlxsText size="sm">Completed at:</PlxsText>
            <PlxsText size="sm">
              {formatScheduledDate(completedDateTime).date}{" "}
              {formatScheduledDate(completedDateTime).time}
            </PlxsText>
          </>
        )}

        <PlxsText size="sm">Date:</PlxsText>
        <PlxsText size="sm">
          {date} {time}
        </PlxsText>

        <PlxsText size="sm">Account name:</PlxsText>
        <PlxsText size="sm">{accountName}</PlxsText>

        <PlxsText size="sm">Draw No:</PlxsText>
        <PlxsText size="sm">{drawNo}</PlxsText>

        <PlxsText size="sm">Draw name:</PlxsText>
        <PlxsText size="sm">{drawName}</PlxsText>

        <PlxsText size="sm">Organisation:</PlxsText>
        <PlxsText size="sm">{organisation}</PlxsText>

        <PlxsText size="sm">Entries count:</PlxsText>
        <PlxsText size="sm">{entryCount || "0"}</PlxsText>

        <PlxsText size="sm">Prize types:</PlxsText>
        <PlxsText size="sm">{prizeTypes}</PlxsText>

        <PlxsText size="sm">Prize winners:</PlxsText>
        <PlxsText size="sm">{prizeWinners}</PlxsText>

        <PlxsText size="sm">Reserve winners:</PlxsText>
        <PlxsText size="sm">{reserveWinners}</PlxsText>

        {paymentRequired !== undefined && (
          <>
            <PlxsText size="sm">Payment status:</PlxsText>
            <PlxsChip
              dataTestId="draw-detail-payment-status-chip"
              label={paymentRequired ? "Payment required" : "Paid"}
              tone={paymentRequired ? "caution" : "positive"}
            />
          </>
        )}

        {scrutineerSettings?.scrutineer_required && (
          <>
            <PlxsText size="sm">Scrutineer required:</PlxsText>
            <PlxsText size="sm">Yes</PlxsText>

            {scrutineerSettings.scrutineer_name && (
              <>
                <PlxsText size="sm">Scrutineer name:</PlxsText>
                <PlxsText size="sm">{scrutineerSettings.scrutineer_name}</PlxsText>
              </>
            )}

            {scrutineerSettings.scrutineer_email && (
              <>
                <PlxsText size="sm">Scrutineer email:</PlxsText>
                <PlxsText size="sm">{scrutineerSettings.scrutineer_email}</PlxsText>
              </>
            )}

            {scrutineerSettings.scrutineer_job_title && (
              <>
                <PlxsText size="sm">Scrutineer job title:</PlxsText>
                <PlxsText size="sm">{scrutineerSettings.scrutineer_job_title}</PlxsText>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DrawStatusInfo;
